<script setup>
import SaveCallbackRequestAction from '@frontJs/components/actions/SaveCallbackRequestAction.vue';
import ToggleCallbackRequestProcessedAtAction from '@frontJs/components/actions/ToggleCallbackRequestProcessedAtAction.vue';
import CheckIcon from '@iconify-vue/ph/check';
import FileTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import XIcon from '@iconify-vue/ph/x';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastSaved } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { toProcess, processed, callback } = defineProps({
    toProcess: {
        type: Boolean,
        required: false,
    },
    processed: {
        type: Boolean,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'tenants.name',
            header: () => 'Client',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'firstname',
            header: () => 'Prénom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.firstname,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'lastname',
            header: () => 'Nom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.lastname,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'email',
            header: () => 'Email',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.email,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'phone',
            header: () => 'Téléphone',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.phone,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'subject',
            header: () => 'Sujet',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.subject,
                }),
        }),
        columnHelper.display({
            id: 'details',
            header: 'Détails',
            cell: (innerProps) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            innerProps.row.original.description
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Description',
                                          content:
                                              innerProps.row.original
                                                  .description,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir la description',
                                                  type: 'primary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : h('div', { class: 'size-8' }, ' '),
                            innerProps.row.original.comment
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Commentaire',
                                          content:
                                              innerProps.row.original.comment,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir le commentaire',
                                                  type: 'secondary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : h('div', { class: 'size-8' }, ' '),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'processedAt',
            header: () => 'Traitée le',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.processedAt,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'createdAt',
            header: () => 'Créé le',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.createdAt,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: (props) => {
                const onSaved = () => {
                    toastSaved();

                    if (callback) {
                        callback();
                    }

                    updateData();
                };

                return h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(
                                ToggleCallbackRequestProcessedAtAction,
                                {
                                    callbackRequest: props.row.original,
                                    callback: onSaved,
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            title:
                                                props.row.original
                                                    .processedAt === null
                                                    ? 'Traiter'
                                                    : 'Détraiter',
                                            type: 'secondary',
                                            callback: () => action(),
                                            icon:
                                                props.row.original
                                                    .processedAt === null
                                                    ? CheckIcon
                                                    : XIcon,
                                            loading: loading,
                                        }),
                                },
                            ),
                            h(
                                SaveCallbackRequestAction,
                                {
                                    callbackRequest: props.row.original,
                                    callback: onSaved,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            type: 'primary',
                                            square: true,
                                            title: 'Modifier',
                                            icon: PencilSimpleIcon,
                                        }),
                                },
                            ),
                        ],
                    },
                );
            },
            width: 0,
        }),
    ],
    url: route('front.postCallbackRequests'),
    data: {
        toProcess,
        processed,
    },
    columnVisibility: {
        processedAt: processed,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    />
</template>
